import React, { useState } from 'react';

import { LayoutSingleColumn, Page } from '../../components';
import { sendContactEnquiry } from '../../util/api';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import css from './ContactPage.module.css';

const initialValues = {
  name: '',
  email: '',
  enquiryType: 'Booking request',
  bookingId: '',
  message: '',
  website: '',
};

const ContactPage = () => {
  const [formValues, setFormValues] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormValues(previousValues => ({ ...previousValues, [name]: value }));
    setSubmitError(null);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await sendContactEnquiry(formValues);
      setSubmitted(true);
      setFormValues(initialValues);
    } catch (error) {
      setSubmitError(error?.error || 'Your message could not be sent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page title="Contact Hotline Glam">
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main id="main-content" className={css.page}>
          <section className={css.intro}>
            <span className={css.eyebrow}>Contact Hotline Glam</span>
            <h1>Need help? We’re here.</h1>
            <p>
              Have a question about a booking, your account, or becoming an artist? Send us a
              message and we’ll get back to you as soon as possible.
            </p>
          </section>

          <section className={css.card}>
            {submitted ? (
              <div className={css.successMessage} role="status">
                <h2>Message sent</h2>
                <p>Thanks for contacting Hotline Glam. We’ll reply to the email you provided.</p>
                <button className={css.secondaryButton} type="button" onClick={() => setSubmitted(false)}>
                  Send another message
                </button>
              </div>
            ) : (
              <form className={css.form} onSubmit={handleSubmit}>
                <label className={css.field}>
                  <span>Full name *</span>
                  <input
                    type="text"
                    name="name"
                    value={formValues.name}
                    onChange={handleChange}
                    autoComplete="name"
                    maxLength="120"
                    required
                  />
                </label>

                <label className={css.field}>
                  <span>Email *</span>
                  <input
                    type="email"
                    name="email"
                    value={formValues.email}
                    onChange={handleChange}
                    autoComplete="email"
                    maxLength="254"
                    required
                  />
                </label>

                <label className={css.field}>
                  <span>I’m contacting about *</span>
                  <select
                    name="enquiryType"
                    value={formValues.enquiryType}
                    onChange={handleChange}
                    required
                  >
                    <option>Booking request</option>
                    <option>Existing booking</option>
                    <option>Artist listing</option>
                    <option>Client account</option>
                    <option>Payment or refund</option>
                    <option>Technical issue</option>
                    <option>Partnership</option>
                    <option>Other</option>
                  </select>
                </label>

                <label className={css.field}>
                  <span>Booking ID (optional)</span>
                  <input
                    type="text"
                    name="bookingId"
                    value={formValues.bookingId}
                    onChange={handleChange}
                    placeholder="For example, HG-1A2B3C4D"
                    maxLength="80"
                  />
                  <small>You can find this in your Hotline Glam booking email.</small>
                </label>

                <label className={css.field}>
                  <span>Message *</span>
                  <textarea
                    name="message"
                    value={formValues.message}
                    onChange={handleChange}
                    rows="7"
                    maxLength="5000"
                    required
                  />
                </label>

                <label className={css.honeypot} aria-hidden="true">
                  Website
                  <input
                    type="text"
                    name="website"
                    value={formValues.website}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </label>

                {submitError ? (
                  <p className={css.errorMessage} role="alert">
                    {submitError}
                  </p>
                ) : null}

                <button className={css.submitButton} type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Submit'}
                </button>
              </form>
            )}

          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ContactPage;
